/***
 * This is the entry point for the client side of the application.
 */
import { CacheProvider, ThemeProvider } from '@emotion/react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { AuthProvider } from './components/internal/AuthProvider';
import { HashScrollListener } from './components/internal/HashScrollListener';
import { RouteChangeListener } from './components/internal/RouteChangeListener';
import { SiteRoot } from './components/internal/SiteRoot';
import { SnackbarProvider } from './components/internal/SnackbarProvider';
import { isClient } from './globals';
import { Site } from './shared/models/site.model';
import reportWebVitals from './reportWebVitals';
import { FileStateProvider } from './state/FileContext';
import { FavoritesProvider } from './state/FavoritesContext';
import { FilterStateProvider } from './state/FilterContext';
import { InternalStateProvider } from './state/InternalContext';
import { SearchProvider } from './state/SearchContext';
import { SiteStateProvider } from './state/SiteContext';
import { VerifiedLinkSequenceProvider } from './state/verified-link-sequence.context';
import { State, reduceStateToBookState, reduceStateToFileState, reduceStateToFilterState, reduceStateToInternalState, reduceStateToSiteState } from './state/state-models';
import { getDevBrowserUrlAfterSsrRedirect } from './shared/utils/dev-ssr-browser-url.utils';
import { getDOMParser } from './shared/utils/dom.utils';
import createEmotionCache from './shared/utils/emotion.utils';
import { BookStateProvider } from './state/BookContext';
import { ActiveTogglesProvider } from './state/active-toggles-url.context';

/**
 * CRA dev: `fetch(REACT_APP_SERVER_URL + pathname)` follows HTTP redirects and returns the final HTML,
 * but `window.location` stays on the dev-server URL (e.g. /aaaa.html). React Router then disagrees with SSR state.
 * Sync the address bar to the redirect target (path on current origin) before hydrate.
 * Keep `window.location.hash`; `response.url` never includes the original fragment.
 */
function syncDevBrowserUrlAfterSsrFetch(response: Response): void {
  if (!response.redirected) {
    return;
  }
  try {
    const next = getDevBrowserUrlAfterSsrRedirect(response.url, {
      pathname: window.location.pathname,
      search: window.location.search,
      hash: window.location.hash,
    });
    if (next == null) {
      return;
    }
    window.history.replaceState(null, "", next);
  } catch {
    // Ignore invalid response.url or other URL parsing failures.
  }
}

if (isClient && process.env.NODE_ENV === "development") {
  const serverUrl = process.env.REACT_APP_SERVER_URL || "https://localhost:3000";
  const url: string = serverUrl + window.location.pathname + window.location.search;
  fetch(url, {
    method: "GET",
    credentials: 'include',
    headers: {
      "ngrok-skip-browser-warning": "true" //needed for ngrok to remove cors error
    },
  }
  ).then(async (response) => {
    syncDevBrowserUrlAfterSsrFetch(response);
    const text = await response.text();
    let htmlText: string = "";
    try {
      const json = JSON.parse(text);
      if (json.success) {
        htmlText = json.body;
      } else {
        const root = document.getElementById('root');
        createRoot(root!).render(<div>No site found!</div>);
      }
    } catch (err: any) {
      if (err.message.includes("not valid JSON")) {
        htmlText = text;
      } else {
        throw err;
      }
    }
    if (htmlText.length > 0) {
      const parser = getDOMParser();
      const parsedHtml = parser.parseFromString(htmlText, 'text/html');
      const serverHead = parsedHtml.documentElement.querySelector("head");
      const serverBody = parsedHtml.documentElement.querySelector("body");
      if (serverHead && serverBody) {
        const t0 = performance.now();
        const importedHead = window.document.importNode(serverHead, true);
        const importedBody = window.document.importNode(serverBody, true);
        // window.document.documentElement.replaceChild(importedHead, window.document.head);
        // window.document.documentElement.replaceChild(importedBody, window.document.body);
        // window.document.documentElement.innerHTML = parsedHtml.documentElement.innerHTML;
        const links: Array<HTMLLinkElement> = Array.from(importedHead.querySelectorAll("link"));
        for (let link of links) {
          if (link.rel === "stylesheet") {
            const newLink = document.createElement("link");
            newLink.rel = "stylesheet";
            newLink.href = link.href;
            const appIndex = newLink.href.indexOf("/api/app/file/");
            if (appIndex !== -1) {
              newLink.href = serverUrl + "/api/app/file/" + newLink.href.slice(appIndex + 14);
            }
            importedHead.appendChild(newLink);
            importedHead.removeChild(link);
          }
        }
        window.document.documentElement.replaceChild(importedHead, window.document.head);
        window.document.documentElement.replaceChild(importedBody, window.document.body);
        const t1 = performance.now();
        console.log("Dev mode HTML replacement time for head and body", t1 - t0);
        hydratePage();
      }
    }
  })
} else {
  hydratePage();
}

function hydratePage() {
  // Fetch site info from meta tags in head, where the server puts it
  const siteDef: Element | null = document?.head?.querySelector("[name=site]");
  const domainDef: Element | null = document?.head?.querySelector("[name=domain]");
  const titleDef: Element | null = document?.head?.querySelector("[name=title]");
  const descriptionDef: Element | null = document?.head?.querySelector("[name=description]");
  const indexcontentDef: Element | null = document?.head?.querySelector("[name=indexContent]");
  const stateDef: Element | null = document?.head?.querySelector("[name=state]");

  const sitename: string = (siteDef && siteDef.getAttribute("content")) || "";
  const domain: string = (domainDef && domainDef.getAttribute("content")) || "";
  const title: string = (titleDef && titleDef.getAttribute("content")) || "";
  const description: string = (descriptionDef && descriptionDef.getAttribute("content")) || "";
  const indexcontentB64 = (indexcontentDef && indexcontentDef.getAttribute("content")) || "";
  const stateB64 = (stateDef && stateDef.getAttribute("content")) || "";

  siteDef && siteDef.remove();
  domainDef && domainDef.remove();
  titleDef && titleDef.remove();
  descriptionDef && descriptionDef.remove();
  indexcontentDef && indexcontentDef.remove();
  stateDef && stateDef.remove();

  const indexcontent = decodeBase64(indexcontentB64);

  const initialState: State = stateB64 ? JSON.parse(decodeBase64(stateB64)) : {};

  if (window.location.protocol === "file:") {
    // TODO: Set site path
    // When running locally (in downloaded package), the site path is the path to the root
    // of the zip archive.
    // The url path is the path to the current page within the site.
    // To get the site path, we need to remove the url path from the end of the url.
    // const urlPathLevels: number = initialState.urlPath?.split("/").length || 0;
    // const locationLevels: number = window.location.pathname.split("/").length || 0;
    // initialState.sitePath = window.location.pathname.split("/", locationLevels - urlPathLevels + 1).join("/");
  }

  const site: Site | null = sitename ? {
    _id: { str: "" },
    name: sitename,
    domain: domain,
    title: title,
    description: description,
  } : null;

  const cache = createEmotionCache();
  let root = document.getElementById('root');
  if (site && root) {
    const siteState = reduceStateToSiteState(initialState);
    const filterState = reduceStateToFilterState(initialState);
    const internalState = reduceStateToInternalState(initialState);
    const fileState = reduceStateToFileState(initialState);
    const bookState = reduceStateToBookState(initialState);
    const t0 = performance.now();
    hydrateRoot(root,
      <SnackbarProvider>
        <CacheProvider value={cache}>
          <ThemeProvider theme={{}}>
            <BrowserRouter>
              <ActiveTogglesProvider>
                <SiteStateProvider initialState={siteState}>
                  <FavoritesProvider>
                    <FilterStateProvider initialState={filterState}>
                      <FileStateProvider initialState={fileState}>
                        <BookStateProvider initialState={bookState}>
                        <InternalStateProvider initialState={internalState}>
                        <AuthProvider>
                          <VerifiedLinkSequenceProvider>
                          <SearchProvider>
                            <SiteRoot indexcontent={indexcontent} />
                            <RouteChangeListener />
                            <HashScrollListener />
                          </SearchProvider>
                          </VerifiedLinkSequenceProvider>
                        </AuthProvider>
                        </InternalStateProvider>
                        </BookStateProvider>
                      </FileStateProvider>
                    </FilterStateProvider>
                  </FavoritesProvider>
                </SiteStateProvider>
              </ActiveTogglesProvider>
            </BrowserRouter>
          </ThemeProvider>
        </CacheProvider>
      </SnackbarProvider>
    );
    const t1 = performance.now();
    console.log("Hydration time", t1 - t0);
  }
  else {
    if (root) {
      createRoot(root).render(<div>No site found!</div>);
    }
  }
}

function decodeBase64(s: string): string {
  return new TextDecoder().decode(Uint8Array.from(window.atob(s), (m) => m.charCodeAt(0)));
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
